import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import axios from "../../../services/axiosInstance";
import {useStoreWebSocket} from "../../../services/StoreWebsocketContext";
import StoreChatBoard from "../../../page/web/store/StoreChatBoard";
import {FiSearch} from "react-icons/fi";
import {TbBuildingStore} from "react-icons/tb";
import "../../../css/web/UserPageRenew.css";

/**
 * 상점사 프로필 아이콘
 *
 *  상점사는 프로필 이미지가 없으므로, 상점 식별번호로 색을 정해
 *  상점마다 항상 같은 색의 '상점' 아이콘을 보여준다.
 *  (목록을 다시 불러도 색이 바뀌지 않는다)
 *
 *  ※ 컴포넌트 안에서 정의하면 부모가 렌더될 때마다 새 타입이 되어
 *    아이콘이 통째로 다시 마운트되므로 반드시 바깥에 둔다.
 */
const STORE_AVATAR_COLORS = [
    {from: "#FFD79A", to: "#FFBE48", icon: "#7A5210"}, // 여명
    {from: "#DCCBFF", to: "#CBB6FF", icon: "#4B3A7A"}, // 라일락
    {from: "#C6EDF2", to: "#A8DDE4", icon: "#1F5A63"}, // 미스트랄
    {from: "#FFD2C7", to: "#FFB3A0", icon: "#8A3B29"}, // 블러쉬
    {from: "#CDEBD3", to: "#A8DDB5", icon: "#2C6238"}, // 민트
    {from: "#D4DCF7", to: "#B4C2EF", icon: "#33417A"}, // 블루
];

function StoreAvatar({store}) {
    const key = Number(store.storeSeqNum) || 0;
    const tone = STORE_AVATAR_COLORS[key % STORE_AVATAR_COLORS.length];
    return (
        <div aria-hidden="true"
             style={{
                 flex: "0 0 auto", width: "40px", height: "40px", borderRadius: "13px",
                 display: "flex", alignItems: "center", justifyContent: "center",
                 // 그라데이션이 적용되지 않는 환경에서도 색이 남도록 단색을 함께 지정한다
                 backgroundColor: tone.to,
                 backgroundImage: `linear-gradient(135deg, ${tone.from}, ${tone.to})`,
                 boxShadow: "0 3px 8px -5px rgba(0,0,0,0.45)",
             }}>
            <TbBuildingStore style={{fontSize: "21px", color: tone.icon}}/>
        </div>
    );
}

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  관리자 채팅 [상점사] 탭
 * ════════════════════════════════════════════════════════════════════════════════
 *  AdminOrderChatPg.js(웹) / MAdminOrderChatPg.js(모바일웹) 양쪽에서 함께 사용한다.
 *
 *  좌측 : 채팅이 존재하는 상점사 목록 + 상점별 미확인 메세지 수
 *  우측 : 선택한 상점사와의 대화 (StoreChatBoard 공용 보드)
 *         - 이모티콘 / 파일첨부 / 매크로
 *         - 관리자 본인이 보낸 메세지에만 [수정][삭제]
 *
 *  실시간 : '/topic/storeChatList' 수신 시 좌측 목록을 갱신한다.
 *          (개별 대화방 실시간 수신은 StoreChatBoard 내부에서 처리)
 *
 *  @param compact true 면 좌우 분할 대신 세로배치 (모바일웹 관리자)
 * ════════════════════════════════════════════════════════════════════════════════
 */
function AdminStoreChatTab({compact, onUnreadTotalChange}) {

    const LCAT = localStorage.getItem('adminToken');
    const authHeader = useMemo(() => ({headers: {Authorization: LCAT}}), [LCAT]);
    /*
     * [중요] 상점사 채팅목록은 '상점사 전용 웹소켓' 으로 구독한다.
     *  기존처럼 회원용 WebsocketContext 에 websocketConnect('adminStoreChat') 로 붙으면,
     *  관리자 본인의 /user/{mbNum}/queue/... 커넥션보다 이 탭이 먼저 열렸을 때
     *  이후 회원용 연결요청이 'adminStoreChat' key 로 맺어진 커넥션을 그대로 돌려받아
     *  관리자 개인 알림/채팅 수신이 끊긴다. 커넥션을 분리해 그 간섭을 없앤다.
     */
    const {storeSubscribe} = useStoreWebSocket();

    const [storeList, setStoreList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [macroList, setMacroList] = useState([]);

    // ────────────────────────────────────────────────────────────────────────
    //  좌측 상점사 목록
    // ────────────────────────────────────────────────────────────────────────

    /*
     * [중요] 목록 내용이 그대로면 state 를 갈아끼우지 않는다.
     *  이 함수는 대화를 조회할 때마다(읽음처리 반영) 호출되는데,
     *  매번 새 배열을 넣으면 참조가 달라져 무조건 리렌더가 일어난다.
     *  그 리렌더가 대화보드의 이펙트를 다시 돌려 강제 하단스크롤을 유발했다.
     */
    const storeListRef = useRef('');
    const selectStoreList = useCallback(() => {
        axios.get('/storeChat/admin/storeList', authHeader)
            .then((res) => {
                const list = res.data || [];
                const signature = JSON.stringify(list);
                if (signature !== storeListRef.current) {
                    storeListRef.current = signature;
                    setStoreList(list);
                }
                if (onUnreadTotalChange) {
                    onUnreadTotalChange(list.reduce((acc, s) => acc + Number(s.unreadTotal || 0), 0));
                }
            })
            .catch(() => {
                storeListRef.current = '[]';
                setStoreList([]);
            });
    }, [authHeader, onUnreadTotalChange]);

    useEffect(() => {
        selectStoreList();
        // 매크로 목록 (기존 주문서/비회원 채팅탭과 동일한 소스)
        axios.get('/member/selectMacroList/', authHeader)
            // [{title, macroDt:[{title, content}]}] 구조를 그대로 넘긴다.
            // 대분류를 눌러 소분류를 펼치는 방식이라 평탄화하면 안 된다. (주문서채팅과 동일)
            .then((res) => setMacroList(res.data || []))
            .catch(() => setMacroList([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 목록 실시간 갱신
    //  storeSubscribe 는 연결 전에 호출해도 되고(연결완료 시 자동구독),
    //  끊겼다 붙어도 자동으로 재구독된다. selectStoreList 는 ref 로 최신값을 참조한다.
    const selectStoreListRef = useRef(selectStoreList);
    useEffect(() => {
        selectStoreListRef.current = selectStoreList;
    }, [selectStoreList]);

    useEffect(() => {
        return storeSubscribe('adminStoreChat', '/topic/storeChatList', () => selectStoreListRef.current());
    }, [storeSubscribe]);

    const filtered = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        if (!q) return storeList;
        return storeList.filter((s) =>
            [s.companyName, s.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
    }, [storeList, searchInput]);

    const storeClick = (store) => {
        setSelected(store);
        // 읽음처리는 /admin/messages 조회 시 서버에서 수행되므로 목록만 갱신한다
        setTimeout(selectStoreList, 400);
    };

    // ────────────────────────────────────────────────────────────────────────
    //  StoreChatBoard 연동 함수
    // ────────────────────────────────────────────────────────────────────────

    const loadMessages = useCallback(async () => {
        if (!selected) return [];
        const res = await axios.get('/storeChat/admin/messages', {
            ...authHeader, params: {storeSeqNum: selected.storeSeqNum},
        });
        return res.data || [];
    }, [selected, authHeader]);

    const sendMessage = useCallback(async (payload) => {
        await axios.post('/storeChat/admin/send', {...payload, chatRole: '0'}, authHeader);
        selectStoreList();
    }, [authHeader, selectStoreList]);

    const modifyMessage = useCallback(async (seqNum, content) => {
        await axios.post('/storeChat/admin/modify', {content},
            {...authHeader, params: {seqNum, storeSeqNum: selected?.storeSeqNum}});
    }, [authHeader, selected]);

    const deleteMessage = useCallback(async (seqNum) => {
        await axios.post('/storeChat/admin/delete', null,
            {...authHeader, params: {seqNum, storeSeqNum: selected?.storeSeqNum}});
        selectStoreList();
    }, [authHeader, selected, selectStoreList]);

    /*
     * 대화를 읽는 즉시 좌측 목록의 미확인 수를 다시 센다.
     *  인라인 함수로 넘기면 대화보드의 조회함수 정체성이 매 렌더 바뀌어
     *  읽던 위치가 계속 바닥으로 끌려 내려가므로 반드시 고정해서 넘긴다.
     */
    const handleBoardRead = useCallback(() => {
        selectStoreListRef.current();
    }, []);

    // ────────────────────────────────────────────────────────────────────────

    /* 검색 — 모바일에서는 목록 위에 따로 놓는다 (비회원 탭과 동일) */
    const searchBox = (
        <div style={{
            display: "flex", alignItems: "center", gap: "7px",
            border: "1px solid var(--up-border-2)", borderRadius: "9px", padding: "0 11px", height: "38px",
            background: "var(--up-surface)",
        }}>
            <FiSearch style={{opacity: 0.5, flex: "0 0 auto"}}/>
            <input placeholder="상호명 / 이메일" value={searchInput} maxLength={40}
                   onChange={(e) => setSearchInput(e.target.value)}
                   style={{
                       border: "none", outline: "none", background: "transparent",
                       width: "100%", fontSize: "13px", color: "var(--up-text)",
                   }}/>
        </div>
    );

    const listPanel = (
        <div className="up-card" style={{
            display: "flex", flexDirection: "column",
            width: compact ? "100%" : "320px", flexShrink: 0,
            height: compact ? "calc(100vh - 300px)" : "calc(100vh - 250px)",
            minHeight: compact ? "320px" : "440px",
        }}>
            {!compact ? (
                <div style={{padding: "12px 14px", borderBottom: "1px solid var(--up-border)", background: "var(--up-panel)"}}>
                    {searchBox}
                </div>
            ) : null}

            <div style={{flex: 1, overflowY: "auto"}}>
                {filtered.length === 0 ?
                    <div style={{padding: "48px 16px", textAlign: "center", fontSize: "13px", color: "var(--up-text-faint)"}}>
                        상점사 채팅데이터가 없습니다
                    </div>
                    :
                    filtered.map((s) => {
                        const active = selected && selected.storeSeqNum === s.storeSeqNum;
                        const unread = Number(s.unreadTotal || 0);
                        return (
                            <div key={s.storeSeqNum} onClick={() => storeClick(s)}
                                 style={{
                                     display: "flex", alignItems: "center", gap: "10px",
                                     padding: "13px 14px", cursor: "pointer",
                                     borderBottom: "1px solid var(--up-border)",
                                     background: active ? "var(--up-brand-soft)" : "transparent",
                                 }}>
                                <StoreAvatar store={s}/>
                                <div style={{minWidth: 0, flex: 1}}>
                                    <p style={{
                                        margin: "0 0 3px", fontSize: "13.5px", fontWeight: "bold",
                                        color: "var(--up-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>{s.companyName || '(상호명 없음)'}</p>
                                    {/* 마지막 대화내용 — 길면 폭에 맞춰 '...' 로 잘린다.
                                        안읽은 메세지가 있으면 눈에 띄도록 진하게 보여준다. */}
                                    <p style={{
                                        margin: 0, fontSize: "11.5px",
                                        color: unread > 0 ? "var(--up-text-sub)" : "var(--up-text-faint)",
                                        fontWeight: unread > 0 ? "bold" : "normal",
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>{s.lastMessage || '대화내용이 없습니다'}</p>
                                </div>
                                {unread > 0 &&
                                    <span style={{
                                        flex: "0 0 auto", minWidth: "20px", height: "20px", padding: "0 6px",
                                        borderRadius: "999px", background: "#FF7962", color: "#FFFFFF",
                                        fontSize: "11px", fontWeight: "bold",
                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    }}>{unread > 99 ? '99+' : unread}</span>
                                }
                            </div>
                        );
                    })
                }
            </div>
        </div>
    );

    const boardPanel = selected ?
        // minHeight:0 이 있어야 모바일 전체화면에서 보드가 남은 높이에 정확히 들어간다
        <div style={{flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column"}}>
            <StoreChatBoard
                key={selected.storeSeqNum}
                role="0"
                storeSeqNum={selected.storeSeqNum}
                headerName={selected.companyName || selected.email}
                loadMessages={loadMessages}
                sendMessage={sendMessage}
                enableMacro
                enableEdit
                onModify={modifyMessage}
                onDelete={deleteMessage}
                macroList={macroList}
                onRead={handleBoardRead}
                mobile={!!compact}
                /*
                 * 모바일은 전체화면이다.
                 *  상단바 높이를 px 로 빼면(예: 100vh - 61px) 상단바가 한 줄이라도 달라질 때 어긋난다.
                 *  바깥 컨테이너가 --app-vvh(= 실제로 보이는 높이)로 잡혀 있으므로
                 *  보드는 남은 공간을 100% 로 받아쓴다.
                 *  최소높이를 두면 키보드가 올라왔을 때 입력창이 화면 밖으로 밀리므로 0 으로 둔다.
                 */
                height={compact ? "100%" : "calc(100vh - 250px)"}
                minHeight={compact ? 0 : undefined}/>
        </div>
        :
        <div className="up-card" style={{
            flex: 1, minWidth: 0,
            height: compact ? "calc(100vh - 420px)" : "calc(100vh - 250px)", minHeight: "360px",
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <p style={{margin: 0, fontSize: "13.5px", color: "var(--up-text-faint)"}}>
                좌측에서 상점사를 선택하면 대화가 열립니다
            </p>
        </div>;

    /*
     * 모바일웹은 목록과 대화를 같은 화면에 쌓지 않는다.
     *  예전에는 위에 목록 / 아래에 채팅이라 메세지를 보낼 때마다 아래로 스크롤해야 했다.
     *  비회원 탭처럼 '목록 -> 선택 -> 대화화면' 으로 전환한다.
     */
    if (compact) {
        /*
         * 대화방은 화면 전체를 덮는다.
         *  예전에는 목록 아래에 붙어 화면의 절반 정도만 차지해 메세지가 몇 줄 보이지 않았다.
         *  비회원 채팅과 동일하게 전체화면 + 상단 닫기 버튼 구성으로 맞춘다.
         */
        if (selected) {
            return (
                <div className="up-scope" data-theme="light"
                     style={{
                         /*
                          * inset:0 (= 100% 높이) 대신 '실제로 보이는 영역' 에 맞춘다.
                          *  디바이스 키보드가 올라오면 --app-vvh 가 줄고 --app-vvt 만큼 밀리므로
                          *  입력창이 항상 키보드 바로 위에 붙는다. (변수가 없으면 기존 100vh 동작)
                          */
                         position: "fixed", left: 0, right: 0,
                         top: "var(--app-vvt, 0px)",
                         height: "var(--app-vvh, 100vh)",
                         zIndex: 1000,
                         display: "flex", flexDirection: "column",
                         background: "var(--up-bg)",
                     }}>
                    {/*상단바 — 상호명 + 닫기*/}
                    <div style={{
                        display: "flex", alignItems: "center", gap: "10px", flexShrink: 0,
                        padding: "12px 14px", borderBottom: "1px solid var(--up-border)",
                        background: "var(--up-panel)",
                    }}>
                        <button onClick={() => setSelected(null)} className="up-btn-ghost"
                                aria-label="목록으로 돌아가기"
                                style={{height: "36px", padding: "0 12px", fontSize: "13px", flexShrink: 0}}>
                            ← 목록
                        </button>
                        <p style={{
                            margin: 0, flex: 1, minWidth: 0,
                            fontSize: "15px", fontWeight: "bold", color: "var(--up-text)",
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {selected.companyName || selected.email}
                        </p>
                        <button onClick={() => setSelected(null)} className="up-btn-ghost"
                                aria-label="닫기"
                                style={{height: "36px", width: "36px", fontSize: "17px", flexShrink: 0}}>
                            ×
                        </button>
                    </div>

                    {/*대화 — 남은 높이를 전부 사용*/}
                    <div style={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column"}}>
                        {boardPanel}
                    </div>
                </div>
            );
        }

        return (
            <div className="up-scope" data-theme="light"
                 style={{padding: "12px", boxSizing: "border-box", width: "100%", marginTop: "10px"}}>
                <div style={{marginBottom: "10px"}}>{searchBox}</div>
                {listPanel}
            </div>
        );
    }

    return (
        <div className="up-scope" data-theme="light"
             style={{
                 display: "flex", flexDirection: "row", gap: "14px",
                 padding: "0 30px 30px", boxSizing: "border-box", width: "100%", marginTop: "10px"
             }}>
            {listPanel}
            {boardPanel}
        </div>
    );
}

export default AdminStoreChatTab;
